'use client';

import React from 'react';
import { Tab as MuiTab, Tabs as MuiTabs, Stack } from '@unity/cloud-ui';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';

export const Tab = (_: z.input<typeof tabSchema>) => null;

export const Tabs = (props: z.input<typeof tabsSchema>) => {
  const [tabIndex, setTabIndex] = React.useState(0);

  const tabContent =
    React.Children.map(props.children, (child) => {
      if (React.isValidElement<z.input<typeof tabSchema>>(child)) {
        return {
          title: child.props.title,
          children: child.props.children,
        };
      }
      return null;
    })?.filter(Boolean) || [];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <>
      <MuiTabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={({ palette }) => ({ borderBottom: `1px solid ${palette.divider}`, m: 0 })}
      >
        {tabContent.map(({ title }, index) => {
          return <MuiTab label={title} key={index} />;
        })}
      </MuiTabs>
      <Stack role="tabpanel" gap={1} padding={2}>
        {tabContent[tabIndex]?.children}
      </Stack>
    </>
  );
};

const documentationURL = getComponentDocumentationUrl('tabs');

const descriptions = {
  tab: {
    title: 'The title of the tab.',
    children: 'The content of the tab.',
    schema: `Creates a tab with a title and content.\n\n\n\n**Example**\n\`\`\`mdx\n<Tab title="Title">\n  The content of the tab\n</Tab>\n\`\`\`\n\n${documentationURL}`,
  },
  tabs: {
    children: 'The list of tabs.',
    schema: `Organize content into tabs. \n\n\n\n**Example**\n\`\`\`mdx\n<Tabs>\n  <Tab title="Title">\n    The content of the tab\n  </Tab>\n</Tabs>\n\`\`\`\n\n${documentationURL}`,
  },
};

export const tabSchema = z
  .object({
    title: z.string().describe(descriptions.tab.title),
    children: z.any().describe(descriptions.tab.children),
  })
  .describe(descriptions.tab.schema);

export const tabsSchema = z
  .object({
    children: z.any().describe(descriptions.tabs.children),
  })
  .describe(descriptions.tabs.schema);

export default Tabs;
